import React from 'react'
import TextInput from '../../../../components/FormElements/TextInput'
import {
  AsyncDualSelect,
  SelectInput,
  ToggleInput,
  Tooltip,
} from '@demandlocal/components'
import states from '../../../../lib/utils/states'
import { IndustryOptions } from '../EditDealership'
import { useGetOrganizations } from '../../../../api/admin/useGetOrganizations'
import { motion } from 'framer-motion'
import { ReactComponent as RemoveIcon } from '../../../../images/icons/remove.svg'
import countries from '../../../../lib/utils/countries'

type DealershipInfoProps = {
  formContext: any
}

export default function DealershipInfo({
  formContext,
}: DealershipInfoProps): JSX.Element {
  const { setValue, getValues, control, watch } = formContext

  const assignedOrganizationsValues = watch(`organizations`)
  const country = watch('country')

  return (
    <div className={'pt-8 pb-8 flex flex-col gap-5'}>
      <div>
        <TextInput
          rules={{ required: 'This field is required' }}
          inverted
          placeholder={'Name'}
          name={'dealership'}
          label={'Name'}
        />
      </div>
      <div className={'flex flex-row gap-5'}>
        <div className={'w-1/2'}>
          <TextInput
            inverted
            placeholder={'Address'}
            name={'address'}
            label={'Address'}
          />
        </div>
        <div className={'w-1/2'}>
          <TextInput inverted placeholder={'City'} name={'city'} label={'City'} />
        </div>
      </div>
      <div className="flex flex-row gap-5">
        <div className={'w-1/3'}>
          <SelectInput
            label={'State'}
            placeholder={'Choose state'}
            name={'state'}
            options={states.filter((state) => state.country === country?.value)}
            isMulti={false}
          />
        </div>
        <div className={'w-1/3'}>
          <SelectInput
            label={'Country'}
            placeholder={'Choose country'}
            name={'country'}
            options={countries}
            isMulti={false}
            onChange={() => setValue('state', null)}
          />
        </div>
        <div className={'w-1/3'}>
          <TextInput inverted placeholder={'Zip'} name={'zip'} label={'Zip Code'} />
        </div>
      </div>
      <div className={'grid grid-cols-2 gap-5'}>
        <div>
          <TextInput inverted placeholder={'Email'} name={'email'} label={'Email'} />
        </div>
        <div>
          <TextInput
            inverted
            placeholder={'Contact phone'}
            name={'phone'}
            label={'Contact phone'}
          />
        </div>
      </div>
      <div className={'grid grid-cols-2 gap-5'}>
        <TextInput inverted placeholder={'Website'} name={'url'} label={'Website'} />
        <SelectInput
          label={'Industry'}
          placeholder={'Industry'}
          name={'industry'}
          options={IndustryOptions}
          isMulti={false}
        />
      </div>
      <div>
        <Tooltip
          label={
            'Enabling this will mark the dealership as self service to display less information'
          }>
          <div className={'flex flex-row gap-4 items-center w-1/2'}>
            <ToggleInput name={'self_service'} label={'Self Service'} />
          </div>
        </Tooltip>
      </div>
      <div>
        <Tooltip
          label={
            'Enabling this will show a different overview page focused on LinkOne Performance'
          }>
          <div className={'flex flex-row gap-4 items-center w-1/2'}>
            <ToggleInput name={'performance_overview'} label={'Performance Overview'} />
          </div>
        </Tooltip>
      </div>
      <div className={'flex flex-col pr-5'}>
        <label
          className={`block text-blue-primary text-sm font-Spartan font-bold mb-2`}
          htmlFor={'organizations'}>
          Assign agencies
        </label>
        <div className={'flex flex-shrink-0 mt-4'}>
          <AsyncDualSelect
            optionsListTitle={'Choose agencies'}
            selectedOptionsListTitle={'Selected agencies'}
            buttonTitle={'+ Assign agency'}
            control={control}
            fetchFn={useGetOrganizations}
            labelKeyName={'name'}
            valueKeyName={'id'}
            searchKeyName={'organization_name'}
            name={`organizations`}
          />
          {assignedOrganizationsValues?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className={
                'pl-5 ml-2 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
              }>
              {assignedOrganizationsValues?.map(
                (value: { value: number; label: string }) => {
                  return (
                    <div
                      key={value.value}
                      className={
                        'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                      }>
                      <div className={'flex justify-between text-xs'}>
                        <span className={'font-bold'}>{value.label}</span>
                        <RemoveIcon
                          className={'cursor-pointer ml-2'}
                          onClick={(e) => {
                            e.preventDefault()
                            setValue(
                              `organizations`,
                              getValues(`organizations`).filter(
                                (v: { value: number }) => v.value !== value.value,
                              ),
                            )
                          }}
                        />
                      </div>
                      <span className={'font-medium pt-1 text-xs'}>{value.value}</span>
                    </div>
                  )
                },
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  )
}
